import { useActionState, useEffect, useRef, useState } from "react"
import { Navigate, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { UpgradeOptions } from "@/components/billing/UpgradeOptions"
import { PlanSelectionCard } from "@/components/billing/PlanSelectionCard"
import { AppHeader } from "@/components/dashboard/AppHeader"
import {
  createCheckoutSession,
  createPortalSession,
  getBillingSubscription,
} from "@/lib/billing-api"
import { formatBillingDate } from "@/lib/billing-format"
import { useSession } from "@/lib/session-context"

const CONFIRMATION_ATTEMPTS = 10
const CONFIRMATION_INTERVAL = 1_500
const BILLING_ERROR = "Billing is unavailable. Please try again."
const EMPTY_PLANS = []

const browserRedirect = (url) => window.location.assign(url)

export function PlanBillingScreen({
  onRetryPlans,
  plans = EMPTY_PLANS,
  plansLoadFailed = false,
  plansLoading = false,
  trial = null,
  redirectTo = browserRedirect,
}) {
  const { currentUser, csrfToken, refresh } = useSession()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const confirmationState = searchParams.get("confirmation")
  const checkoutState = searchParams.get("checkout")
  const upgradeState = searchParams.get("upgrade")
  const upgradeTier = searchParams.get("upgrade_tier")
  const isCheckoutConfirmation = checkoutState === "success"
  const isUpgradeConfirmation = upgradeState === "success" && Boolean(upgradeTier)
  const [confirmationTimedOut, setConfirmationTimedOut] = useState(false)
  const [upgradeConfirmationTimedOut, setUpgradeConfirmationTimedOut] = useState(false)
  const [sessionRefreshPending, setSessionRefreshPending] = useState(
    !isCheckoutConfirmation && !isUpgradeConfirmation,
  )
  const [sessionRefreshError, setSessionRefreshError] = useState(null)
  const confirmationToastShownRef = useRef(false)
  const refreshRef = useRef(refresh)
  const subscription = currentUser?.subscription
  const periodEnd = formatBillingDate(subscription?.current_period_end)
  const trialEnd = formatBillingDate(subscription?.trial_ends_at)
  const isTrialing = subscription?.status === "trialing"

  useEffect(() => {
    if (confirmationState !== "confirmed" || confirmationToastShownRef.current) return

    confirmationToastShownRef.current = true
    toast.success("Email confirmed. Choose a plan to start your trial.")

    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.delete("confirmation")
    setSearchParams(nextSearchParams, { replace: true })
  }, [confirmationState, searchParams, setSearchParams])

  useEffect(() => {
    refreshRef.current = refresh
  }, [refresh])

  useEffect(() => {
    if (isCheckoutConfirmation || isUpgradeConfirmation) return undefined

    let cancelled = false

    const refreshSession = async () => {
      try {
        await refreshRef.current()
      } catch {
        if (!cancelled) setSessionRefreshError(BILLING_ERROR)
      } finally {
        if (!cancelled) setSessionRefreshPending(false)
      }
    }

    refreshSession()
    return () => {
      cancelled = true
    }
  }, [isCheckoutConfirmation, isUpgradeConfirmation])

  const [checkoutError, checkoutAction, checkoutPending] = useActionState(
    async (_previous, formData) => {
      try {
        const { url } = await createCheckoutSession(formData.get("tier"), { csrfToken })
        redirectTo(url)
        return null
      } catch {
        return BILLING_ERROR
      }
    },
    null,
  )

  const [portalError, portalAction, portalPending] = useActionState(
    async () => {
      try {
        const { url } = await createPortalSession({ csrfToken })
        redirectTo(url)
        return null
      } catch {
        return BILLING_ERROR
      }
    },
    null,
  )

  const [refreshError, refreshAction, refreshPending] = useActionState(
    async () => {
      try {
        const status = await getBillingSubscription()
        if (!status.access_granted) return "Payment is not confirmed yet. Please try again shortly."

        const refreshedUser = await refreshRef.current()
        if (!refreshedUser?.subscription?.access_granted) {
          return "Payment is not confirmed yet. Please try again shortly."
        }

        navigate("/", { replace: true })
        return null
      } catch {
        return BILLING_ERROR
      }
    },
    null,
  )

  const [upgradeRefreshError, upgradeRefreshAction, upgradeRefreshPending] = useActionState(
    async () => {
      try {
        const status = await getBillingSubscription()
        if (!status.access_granted || status.tier !== upgradeTier) {
          return "Upgrade is not confirmed yet. Please try again shortly."
        }

        const refreshedUser = await refreshRef.current()
        if (refreshedUser?.subscription?.tier !== upgradeTier) {
          return "Upgrade is not confirmed yet. Please try again shortly."
        }

        navigate("/billing", { replace: true })
        return null
      } catch {
        return BILLING_ERROR
      }
    },
    null,
  )

  useEffect(() => {
    if (checkoutState !== "success") return undefined
    if (subscription?.access_granted) {
      navigate("/", { replace: true })
      return undefined
    }

    let cancelled = false
    let timer
    let attempts = 0

    const poll = async () => {
      attempts += 1
      try {
        const status = await getBillingSubscription()
        if (cancelled) return
        if (status.access_granted) {
          const refreshedUser = await refreshRef.current()
          if (cancelled) return
          if (refreshedUser?.subscription?.access_granted) {
            navigate("/", { replace: true })
            return
          }
        }
      } catch {
        // Keep polling through temporary webhook or network delays.
      }

      if (cancelled) return
      if (attempts >= CONFIRMATION_ATTEMPTS) {
        setConfirmationTimedOut(true)
        return
      }
      timer = window.setTimeout(poll, CONFIRMATION_INTERVAL)
    }

    poll()
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [checkoutState, navigate, subscription?.access_granted])

  useEffect(() => {
    if (!isUpgradeConfirmation) return undefined
    if (subscription?.tier === upgradeTier) {
      navigate("/billing", { replace: true })
      return undefined
    }

    let cancelled = false
    let timer
    let attempts = 0

    const poll = async () => {
      attempts += 1
      try {
        const status = await getBillingSubscription()
        if (cancelled) return
        if (status.access_granted && status.tier === upgradeTier) {
          const refreshedUser = await refreshRef.current()
          if (cancelled) return
          if (refreshedUser?.subscription?.tier === upgradeTier) {
            navigate("/billing", { replace: true })
            return
          }
        }
      } catch {
        // Keep polling through temporary webhook or network delays.
      }

      if (cancelled) return
      if (attempts >= CONFIRMATION_ATTEMPTS) {
        setUpgradeConfirmationTimedOut(true)
        return
      }
      timer = window.setTimeout(poll, CONFIRMATION_INTERVAL)
    }

    poll()
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [isUpgradeConfirmation, navigate, subscription?.tier, upgradeTier])

  if (!currentUser) return <Navigate to="/sign-in" replace />

  let billingContent
  if (sessionRefreshPending) {
    billingContent = (
      <Card className="max-w-xl" aria-live="polite">
        <CardHeader>
          <CardTitle>Checking billing status</CardTitle>
          <CardDescription>Loading current subscription details.</CardDescription>
        </CardHeader>
      </Card>
    )
  } else if (subscription?.access_granted) {
    billingContent = (
      <>
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>{subscription.tier_name}</CardTitle>
            <CardDescription>Status: {isTrialing ? "Trial" : subscription.status}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {isTrialing && trialEnd ? <p>Trial ends {trialEnd}.</p> : null}
            {!isTrialing && periodEnd ? <p>Current paid period ends {periodEnd}.</p> : null}
            {subscription.status === "past_due" ? (
              <p className="text-muted-foreground">
                Access remains available while payment is retried. Manage billing to update payment details.
              </p>
            ) : null}
            {subscription.cancel_at_period_end && isTrialing && trialEnd ? (
              <p className="text-muted-foreground">
                Cancellation is scheduled. Access remains available until {trialEnd}. You will not be charged.
              </p>
            ) : null}
            {subscription.cancel_at_period_end && !isTrialing && periodEnd ? (
              <p className="text-muted-foreground">
                Cancellation is scheduled. Access remains available until {periodEnd}.
              </p>
            ) : null}
          </CardContent>
          <CardFooter className="flex-col items-start gap-3">
            <form action={portalAction}>
              <Button type="submit" disabled={portalPending}>
                {portalPending ? "Opening…" : "Manage billing"}
              </Button>
            </form>
            {portalError ? <p className="text-sm text-destructive">{portalError}</p> : null}
          </CardFooter>
        </Card>
        <UpgradeOptions
          plans={plans}
          subscription={subscription}
          csrfToken={csrfToken}
          redirectTo={redirectTo}
          plansLoadFailed={plansLoadFailed}
          plansLoading={plansLoading}
          onRetryPlans={onRetryPlans}
        />
      </>
    )
  } else if (plansLoadFailed) {
    billingContent = (
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>Billing is unavailable</CardTitle>
          <CardDescription>Plans could not be loaded. Please try again.</CardDescription>
        </CardHeader>
        <CardFooter>
          <Button type="button" variant="outline" disabled={plansLoading} onClick={onRetryPlans}>
            {plansLoading ? "Loading…" : "Try again"}
          </Button>
        </CardFooter>
      </Card>
    )
  } else {
    billingContent = (
      <section className="grid gap-6 md:grid-cols-3" aria-label="Subscription plans">
        {plans.map((plan) => (
          <PlanSelectionCard
            key={plan.key}
            checkoutAction={checkoutAction}
            checkoutPending={checkoutPending}
            plan={plan}
            trial={trial}
          />
        ))}
        {checkoutError ? <p className="text-sm text-destructive md:col-span-3">{checkoutError}</p> : null}
      </section>
    )
  }

  return (
    <>
      <AppHeader
        status={!sessionRefreshPending && subscription?.access_granted ? "online" : "standby"}
        planName={sessionRefreshPending ? "Checking plan" : subscription?.tier_name ?? "No plan"}
        showNav={!sessionRefreshPending && Boolean(subscription?.access_granted)}
      />
      <main className="min-h-screen bg-background px-6 py-12 text-foreground">
        <title>Plan & Billing — Sentinel</title>
        <div className="mx-auto flex max-w-5xl flex-col gap-8">
          <header className="space-y-2">
            <p className="text-sm font-medium text-primary">Sentinel OS</p>
            <h1 className="text-3xl font-semibold tracking-tight">Plan & Billing</h1>
            <p className="text-muted-foreground">Manage application access and billing.</p>
          </header>

          {sessionRefreshError ? (
            <p className="text-sm text-destructive" role="alert">{sessionRefreshError}</p>
          ) : null}

          {!sessionRefreshPending && checkoutState === "success" && !subscription?.access_granted ? (
            <Card>
              <CardHeader>
                <CardTitle>Confirming payment</CardTitle>
                <CardDescription>
                  Stripe completed Checkout. Access opens after signed webhook confirmation.
                </CardDescription>
              </CardHeader>
              {confirmationTimedOut ? (
                <CardFooter className="flex-col items-start gap-3">
                  <p className="text-sm text-muted-foreground">
                    Confirmation is taking longer than expected.
                  </p>
                  <form action={refreshAction}>
                    <Button type="submit" variant="outline" disabled={refreshPending}>
                      {refreshPending ? "Checking…" : "Check payment"}
                    </Button>
                  </form>
                  {refreshError ? <p className="text-sm text-destructive">{refreshError}</p> : null}
                </CardFooter>
              ) : null}
            </Card>
          ) : null}

          {isUpgradeConfirmation && subscription?.tier !== upgradeTier ? (
            <Card>
              <CardHeader>
                <CardTitle>Confirming upgrade</CardTitle>
                <CardDescription>
                  Stripe completed the upgrade flow. Your plan updates after signed webhook confirmation.
                </CardDescription>
              </CardHeader>
              {upgradeConfirmationTimedOut ? (
                <CardFooter className="flex-col items-start gap-3">
                  <p className="text-sm text-muted-foreground">
                    Confirmation is taking longer than expected. Your current plan remains active.
                  </p>
                  <form action={upgradeRefreshAction}>
                    <Button type="submit" variant="outline" disabled={upgradeRefreshPending}>
                      {upgradeRefreshPending ? "Checking…" : "Check upgrade"}
                    </Button>
                  </form>
                  {upgradeRefreshError ? (
                    <p className="text-sm text-destructive">{upgradeRefreshError}</p>
                  ) : null}
                </CardFooter>
              ) : null}
            </Card>
          ) : null}

          {billingContent}
        </div>
      </main>
    </>
  )
}
